import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import moment from "moment";
import {
  get_school_roster_api, add_school_student_api, move_student_pipeline_api, get_student_record_api,
} from "../../DAL/SchoolPortal/SchoolPortal";
import PipelineBoard from "./PipelineBoard";
import StudentPopup from "./StudentPopup";
import RecordActionPanel from "./StudentRecord/RecordActionPanel";
import HomeworkModal from "./StudentRecord/HomeworkModal";
import SessionPrep from "./Tutors/SessionPrep";
import { useContentSetting } from "../../Hooks/ContentSetting";
import { useSchool } from "../../Hooks/SchoolContext";
import {
  border, textPrimary, textSecondary, textMuted, accent, font, easing,
  success, warning, error as errorColor, infoBlue, avatarColorFor,
  inputSx, primaryButtonSx, secondaryButtonSx, dialogPaperSx,
} from "../../layouts/schoolPortal/schoolPortalTheme";

// Mirrors TUTOR_CAPABLE_ROLES on the backend. `operations` is deliberately
// absent: the front desk cannot be an instructor, so it can never propose from
// a diary it does not have.
const TUTOR_CAPABLE = ["instructor", "primary_admin", "staff_admin"];

/**
 * THIS PAGE HAS ONE JOB: find a student, and see who is drifting.
 *
 * It is not an analytics page and it is not a dashboard. Every column earns its
 * place by being a fact that decides whether to open someone — where they are,
 * what is wrong, whether they are ready, what they are worth. Anything that
 * does not help you pick a row does not belong here.
 *
 * WHAT IT REPLACED
 * Four tall stat tiles above a table of Name · Email · Status · Last question.
 * The widest column was an email address, none of the four facts that lead to a
 * booking were present, and the page reimplemented "at risk" in the browser —
 * 14 days quiet, 10+ questions, under 60% accuracy — while the server computed
 * a scored version. Two definitions of one word, disagreeing about the same
 * student on two screens.
 *
 * All of it comes from GET /students/roster — the client renders, it does not
 * derive.
 */

const BAND = {
  critical: { color: errorColor, label: "Critical" },
  at_risk:  { color: warning,    label: "At risk" },
  watch:    { color: infoBlue,   label: "Watch" },
  ok:       { color: success,    label: "On track" },
};

// Filters are by COURSE, and only exist when a school sells more than one.
//
// The previous three — At risk, Money problems — duplicated what the board
// already shows: a student in the Struggling column IS the at-risk filter, and
// a card carries its own ⚠ for a failed card. Filtering a board by the thing
// the board is arranged by leaves you looking at one column.
//
// Course is the one axis the board does NOT encode, and the one a school with
// both a PPL and an ATPL programme actually needs.

const money = (n, c) =>
  new Intl.NumberFormat(c === "GBP" ? "en-GB" : "en-US",
    { style: "currency", currency: c || "USD", maximumFractionDigits: 0 }).format(n || 0);

// Length is how much of the course; colour is HEALTH. Shared with the board so
// a student cannot look green on one screen and red on the other. You can be
// 95% done and in the red, or green having barely started.
// Falls back to the score when health is absent, rather than defaulting green —
// an unknown state that renders as "fine" makes a broken screen look like a
// healthy school.
const healthColour = (h, performance) => {
  if (h === "serious") return errorColor;
  if (h === "concern") return warning;
  if (h === "ok") return success;
  if (performance == null) return textMuted;
  return performance >= 85 ? success : performance >= 70 ? warning : errorColor;
};

const lbl = {
  fontFamily: font, fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.06em",
  textTransform: "uppercase", color: textSecondary,
};

export default function SchoolStudents() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  // Who is looking. Not cosmetic: the actions on a card are decided by what the
  // backend will let this role actually do, and offering one it will refuse is
  // how the front desk ended up with a primary button that could never work.
  const { userInfo } = useContentSetting();
  const { isIndependentInstructor } = useSchool();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [course, setCourse] = useState("all");
  const [q, setQ] = useState("");

  // Board first. The list is how you FIND someone; the board is how you see
  // the shape of the school — and the shape is what a school is paying for.
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("school_students_view") || "board"; } catch { return "board"; }
  });
  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem("school_students_view", v); } catch { /* private browsing */ }
  };
  // The natural order answers "who needs me", which is the right default and
  // exactly the wrong thing when you are hunting for one person you can already
  // name. A-Z is that second job. Headers stay labels — the order is a stated
  // choice at the top of the page, not something you discover by clicking a
  // column and watching the list jump.
  const [sort, setSort] = useState(() => {
    try { return localStorage.getItem("school_students_sort") || "natural"; } catch { return "natural"; }
  });
  const setSortPersist = (v) => {
    setSort(v);
    try { localStorage.setItem("school_students_sort", v); } catch { /* private browsing */ }
  };
  const [movingId, setMovingId] = useState(null);
  const [flagFor, setFlagFor] = useState(null);
  // Studying gets no popup — nothing is pending there, so clicking is browsing
  // and it goes straight to the record.
  const [popupFor, setPopupFor] = useState(null);
  const [prepFor, setPrepFor] = useState(null);
  const [panel, setPanel] = useState({ open: false, mode: "propose", student: null });
  // Setting work needs the student's coverage — the subjects, and what they
  // have already answered in each. The board only carries a roster row, so it
  // is fetched on the way in.
  const [homework, setHomework] = useState({ open: false, record: null, studentId: null, loading: false });
  const [flagNote, setFlagNote] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [newStudent, setNewStudent] = useState({ firstName: "", lastName: "", email: "", course: "", phone: "" });
  const [adding, setAdding] = useState(false);

  // A refresh is not a page load. Dropping a card, closing a popup or adding a
  // student all re-fetch the roster, and flipping `loading` back on tore the
  // whole screen down to a centred spinner — headings, tabs, search box and
  // board — for the length of a request, then rebuilt it. The action already
  // says it is working (the card dims, the button reads "Flagging…"), so the
  // roster repaints underneath whatever the user is looking at. Only the first
  // load, which has nothing to show yet, gets the spinner.
  const load = async () => {
    try {
      const r = await get_school_roster_api();
      if (r.code === 200) { setData(r); setError(null); }
      else setError(r.message || "Could not load your roster");
    } catch {
      setError("Could not load your roster");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Only worth showing when there is a choice to make. One course on sale means
  // every student is on it, and a filter with one option is furniture.
  // Course is the ONLY filter. The board's columns already are the stages, so
  // a tab row that filters to a stage was the same control drawn twice —
  // clicking "Struggling" left you looking at one column of a four-column
  // board. Course is the axis the board does not encode.
  const courseTabs = useMemo(() => {
    const cat = data?.catalogue || [];
    if (cat.length < 2) return [];
    return cat.map((c) => ({ key: c.license, label: c.label }));
  }, [data]);


  const rows = useMemo(() => {
    const all = data?.students || [];
    const byTab = all.filter((s) => {
      return course === "all" || s.course === course;
    });
    const needle = q.trim().toLowerCase();
    const searched = needle
      ? byTab.filter((s) => s.name.toLowerCase().includes(needle) || (s.email || "").toLowerCase().includes(needle))
      : byTab;
    // A-Z sorts on the name as it is DISPLAYED. Sorting on surname under a
    // "First Last" column puts the visible first letters out of order, which
    // reads as broken rather than as sorted by surname.
    //
    // The board is deliberately excluded: its columns already carry their own
    // order from the server, and each one means something (Struggling runs
    // oldest-first so the person nobody got to stays on top). An A-Z control
    // over that would either do nothing or quietly break it.
    if (view === "list" && sort === "az") {
      return [...searched].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
    }
    // Most recent first, so the list leads with what changed. Future-dated
    // stages sort soonest-first — the server says which is which. Risk breaks
    // ties, because two students who changed on the same day are not equally
    // worth opening.
    return [...searched].sort((a, b) => {
      const ta = a.stageAt ? new Date(a.stageAt).getTime() : 0;
      const tb = b.stageAt ? new Date(b.stageAt).getTime() : 0;
      if (ta && tb && ta !== tb) {
        // Same rule the board uses, from the same source.
        const dir = (data?.stageSort || {})[a.stage] || "newest";
        return dir === "oldest" ? ta - tb : tb - ta;
      }
      return (b.risk?.score || 0) - (a.risk?.score || 0);
    });
  }, [data, q, course, view, sort]);

  // Dropping a card does the real thing. Offered and Booked are not drop
  // targets and say why rather than silently refusing.
  const handleMove = (student, col) => {
    if (!col.droppable) { enqueueSnackbar(col.why, { variant: "info" }); return; }
    if (col.key === "struggling") { setFlagFor(student); setFlagNote(""); return; }
    commitMove(student, "studying");
  };

  const commitMove = async (student, to, note) => {
    setMovingId(student.id);
    try {
      const r = await move_student_pipeline_api(student.id, to, note);
      // No toast when it works. The card is already sitting in the column you
      // dropped it into — a green box in the corner saying so is the same fact
      // twice, and it lands over the board you are trying to read. Failure
      // still speaks, because nothing on screen shows a move that did not
      // happen.
      if (r.code === 200) await load();
      else enqueueSnackbar(r.message || "Could not move that student", { variant: "error" });
    } catch {
      enqueueSnackbar("Could not move that student", { variant: "error" });
    } finally { setMovingId(null); setFlagFor(null); }
  };

  const openStudent = (student) => {
    if (student.stage === "studying") { navigate(`/school/students/${student.id}`); return; }
    // A booked session that is YOURS is not a decision, it is a briefing — so
    // it opens the brief that already exists rather than a 440px dialog that
    // reimplemented three lines of it and truncated the last summary at 130
    // characters. Everyone else's booked card is a commercial question, and
    // that one is small enough for a modal.
    if (student.stage === "booked" && student.nextSession?.mine && !student.request) {
      setPrepFor({ _id: student.nextSession.id, start_time: student.nextSession.at, student_name: student.name });
      return;
    }
    setPopupFor(student);
  };

  const popupAction = async (kind, student) => {
    if (!kind) { load(); return; }
    setPopupFor(null);

    // SETTING WORK GOES THROUGH THE SAME COMPOSER AS THE RECORD PAGE.
    //
    // "Set a task" here used to open the plain task panel, which writes a
    // title and a due date and nothing else — a task with no target can never
    // be measured and never closes itself, so it sits open for ever showing no
    // progress. That is exactly what it produced: four identical untargeted
    // tasks on one student, none of which could ever have completed. The
    // record page routes this to the homework composer; the board did not, so
    // the same button did two different things depending on where you pressed
    // it.
    if (kind === "task") {
      // Fetched BEFORE the dialog opens, not alongside it. The composer builds
      // its subject list, its suggestions and its "already open" strip out of
      // the record — with none of that it is a dialog with an empty subject
      // picker and no way to submit, which is worse than a moment's wait.
      setHomework({ open: false, record: null, studentId: student.id, loading: true });
      try {
        const r = await get_student_record_api(student.id);
        if (r?.code === 200) {
          setHomework({ open: true, record: r, studentId: student.id, loading: false });
        } else {
          setHomework({ open: false, record: null, studentId: null, loading: false });
          enqueueSnackbar(r?.message || "Could not open that student's record", { variant: "error" });
        }
      } catch (e) {
        setHomework({ open: false, record: null, studentId: null, loading: false });
        enqueueSnackbar("Could not open that student's record", { variant: "error" });
      }
      return;
    }

    setPanel({ open: true, mode: kind, student });
  };

  const handleAdd = async () => {
    if (!newStudent.firstName.trim() || !newStudent.email.trim()) {
      enqueueSnackbar("A first name and an email are required", { variant: "error" });
      return;
    }
    setAdding(true);
    try {
      const r = await add_school_student_api(newStudent);
      if (r.code === 200 || r.code === 201) {
        // Same rule: the dialog closing and the student appearing in the
        // roster is the confirmation.
        setAddOpen(false);
        load();
      } else enqueueSnackbar(r.message || "Could not add that student", { variant: "error" });
    } catch {
      enqueueSnackbar("Could not add that student", { variant: "error" });
    } finally { setAdding(false); }
  };

  // Having nothing to show is the only reason to show a spinner instead of the
  // page. Once a roster has arrived, it stays on screen through every refresh.
  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: accent }} /></Box>;
  }

  const mods = data?.modules || {};

  return (
    // The page is exactly one viewport tall and does not scroll. The heading
    // and controls are a fixed band; the board below them gets whatever is
    // left and scrolls inside its own columns. Sticky positioning was not
    // enough — the layout's <main> is the scroll container, so the whole page
    // moved under the "sticky" header.
    <Box sx={{
      p: { xs: 1.5, sm: 2 }, maxWidth: 1400, margin: "0 auto",
      height: { xs: "auto", md: "100vh" },
      display: "flex", flexDirection: "column", minHeight: 0,
    }}>
      <Box sx={{ flexShrink: 0 }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontFamily: font, fontSize: "20px", fontWeight: 800, color: textPrimary, letterSpacing: "-0.2px" }}>
          Students
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mt: 0.2 }}>
          {view === "list" && sort === "az"
            ? `Everyone ${isIndependentInstructor ? "in your group" : "at your school"}, A–Z`
            : `Everyone ${isIndependentInstructor ? "in your group" : "at your school"}, most urgent first`}
        </Typography>
      </Box>

      {error && (
        <Box sx={{ mb: 1.5, p: 1.25, borderRadius: "8px", border: `1px solid ${warning}`, background: `${warning}0d` }}>
          <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 600, color: warning }}>{error}</Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", flex: 1 }}>
          {/* Course is the only filter, and only when a school sells more than
              one. With a single course every student is on it, and a filter
              with one option is furniture. */}
          {courseTabs.length > 0 && [{ key: "all", label: "All courses" }, ...courseTabs].map((t) => (
            <Box key={t.key} onClick={() => setCourse(t.key)} sx={{
              px: 1.1, py: 0.35, borderRadius: "999px", cursor: "pointer", fontFamily: font,
              fontSize: "12px", fontWeight: 600, transition: `all 0.12s ${easing}`,
              color: course === t.key ? "#fff" : textSecondary,
              backgroundColor: course === t.key ? textPrimary : "transparent",
              border: `1px solid ${course === t.key ? textPrimary : border}`,
            }}>{t.label}</Box>
          ))}
        </Box>
        <TextField
          placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)}
          size="small" InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 17, color: textMuted, mr: 0.75 }} /> }}
          sx={{ ...inputSx, width: { xs: "100%", sm: 230 } }}
        />
        {/* Only in the list, for the same reason the board is excluded from it:
            a switch that does nothing is worse than no switch. */}
        {view === "list" && (
          <Box sx={{ display: "flex", border: `1px solid ${border}`, borderRadius: "8px", overflow: "hidden" }}>
            {[["natural", "Most urgent"], ["az", "A–Z"]].map(([k, label]) => (
              <Box key={k} onClick={() => setSortPersist(k)} sx={{
                px: 1.25, py: 0.75, cursor: "pointer", fontFamily: font, fontSize: "12.5px", fontWeight: 600,
                color: sort === k ? "#fff" : textSecondary,
                backgroundColor: sort === k ? textPrimary : "#fff",
                transition: `all 0.12s ${easing}`,
              }}>{label}</Box>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", border: `1px solid ${border}`, borderRadius: "8px", overflow: "hidden" }}>
          {[["board", "Board"], ["list", "List"]].map(([k, label]) => (
            <Box key={k} onClick={() => setViewPersist(k)} sx={{
              px: 1.25, py: 0.75, cursor: "pointer", fontFamily: font, fontSize: "12.5px", fontWeight: 600,
              color: view === k ? "#fff" : textSecondary,
              backgroundColor: view === k ? textPrimary : "#fff",
              transition: `all 0.12s ${easing}`,
            }}>{label}</Box>
          ))}
        </Box>
        {/* A control, at the weight of a control. This was a 44px gradient pill
            with a glow and a hover-lift, floating in the top corner — shouting
            on a page whose actual primary action is opening a row. Adding a
            student is administrative and occasional; it belongs with the search
            box, not above it. */}
        <Button
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => { setNewStudent({ firstName: "", lastName: "", email: "", course: data?.catalogue?.length === 1 ? data.catalogue[0].license : "", phone: "" }); setAddOpen(true); }}
          sx={{
            textTransform: "none", fontFamily: font, fontSize: "12.5px", fontWeight: 600,
            color: textPrimary, border: `1px solid ${border}`, backgroundColor: "#fff",
            borderRadius: "8px", px: 1.5, py: 0.75, minHeight: 0, whiteSpace: "nowrap",
            "&:hover": { backgroundColor: "#F5F2EF", borderColor: textMuted },
          }}
        >
          Add student
        </Button>
      </Box>

      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {view === "board" ? (
        <PipelineBoard
          students={rows}
          // Column totals come from the whole roster, not the filtered rows —
          // a count that changes when you type in the search box is a count
          // nobody can quote.
          stages={q ? undefined : data?.stages}
          stageSort={data?.stageSort}
          groupSessions={data?.groupSessions || []}
          stageValue={data?.stageValue || {}}
          onMove={handleMove}
          onOpen={openStudent}
          busyId={movingId}
        />
      ) : (
      <Box sx={{ border: `1px solid ${border}`, borderRadius: "10px", backgroundColor: "#fff", overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "100%" }}>
        <Box sx={{ display: "flex", gap: 1.25, px: 1.75, py: 0.9, borderBottom: `1px solid ${border}`, backgroundColor: "#FAF8F5", flexShrink: 0 }}>
          <Typography sx={{ ...lbl, flex: "1 1 200px", minWidth: 0 }}>Student</Typography>
          {mods.learn !== false && <Typography sx={{ ...lbl, width: 128, flexShrink: 0 }}>Done · scoring</Typography>}
          {mods.insights !== false && <Typography sx={{ ...lbl, flex: "1 1 180px", minWidth: 0 }}>Risk</Typography>}
          {mods.insights !== false && <Typography sx={{ ...lbl, width: 92, flexShrink: 0 }}>Written</Typography>}
          {mods.tutors !== false && <Typography sx={{ ...lbl, width: 128, flexShrink: 0 }}>Total spend</Typography>}
          {mods.tutors !== false && <Typography sx={{ ...lbl, width: 104, flexShrink: 0 }}>Next</Typography>}
        </Box>

        {/* Rows scroll under a fixed header, the same way the board's columns
            do, so the column labels never leave the screen. */}
        <Box sx={{
          overflowY: "auto", flex: 1, minHeight: 0,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#D8D2CA", borderRadius: 3 },
        }}>
        {rows.map((s) => {
          const band = s.risk ? BAND[s.risk.band] : null;
          return (
            <Box key={s.id} onClick={() => openStudent(s)} sx={{
              display: "flex", alignItems: "center", gap: 1.25, px: 1.75, py: 1.05, cursor: "pointer",
              borderBottom: `1px solid ${border}`, "&:last-of-type": { borderBottom: "none" },
              transition: `background 0.12s ${easing}`, "&:hover": { backgroundColor: "#FAF8F5" },
            }}>
              <Box sx={{ flex: "1 1 200px", minWidth: 0, display: "flex", alignItems: "center", gap: 1.1 }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: "8px", flexShrink: 0,
                  backgroundColor: avatarColorFor(s.name), color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: font, fontWeight: 700, fontSize: "11px",
                }}>
                  {s.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.name}
                  </Typography>
                  {/* Email is how you confirm you have the right person, not a
                      reason to open them — so it is the second line, not a column. */}
                  <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.email}
                  </Typography>
                </Box>
              </Box>

              {mods.learn !== false && (
                <Box sx={{ width: 128, flexShrink: 0 }}>
                  {s.progress ? (
                    <>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: textPrimary, fontVariantNumeric: "tabular-nums" }}>
                          {s.progress.completion}%
                        </Typography>
                        <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, color: healthColour(s.health, s.progress.performance), fontVariantNumeric: "tabular-nums" }}>
                          {s.progress.performance == null ? "—" : `${s.progress.performance}%`}
                        </Typography>
                      </Box>
                      <Box sx={{ height: 3, borderRadius: 2, backgroundColor: "#F0EDE9", mt: 0.4, overflow: "hidden" }}>
                        <Box sx={{ width: `${s.progress.completion}%`, height: "100%", backgroundColor: healthColour(s.health, s.progress.performance) }} />
                      </Box>
                    </>
                  ) : <Typography sx={{ fontFamily: font, fontSize: "12px", color: textMuted }}>—</Typography>}
                </Box>
              )}

              {mods.insights !== false && (
                <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
                  {band ? (
                    <>
                      <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: band.color }}>
                        {band.label}
                      </Typography>
                      {/* The reason, not the score. A number nobody can act on
                          is not a reason to open someone; a sentence is. */}
                      <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.risk.reason || "no signals"}
                      </Typography>
                    </>
                  ) : <Typography sx={{ fontFamily: font, fontSize: "12px", color: textMuted }}>—</Typography>}
                </Box>
              )}

              {mods.insights !== false && (
                <Box sx={{ width: 92, flexShrink: 0 }}>
                  {s.written?.source === "faa_written" ? (
                    <>
                      <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: s.written.passed ? success : errorColor }}>
                        {s.written.score}%
                      </Typography>
                      <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted }}>
                        {moment(s.written.takenAt).format("D MMM")}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: s.written?.ready ? success : textMuted, fontWeight: s.written?.ready ? 700 : 400 }}>
                      {s.written?.ready ? "Ready" : "Not yet"}
                    </Typography>
                  )}
                </Box>
              )}

              {mods.tutors !== false && (
                <Box sx={{ width: 128, flexShrink: 0 }}>
                  <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: s.value?.failedPayment ? errorColor : textPrimary, fontVariantNumeric: "tabular-nums" }}>
                    {money(s.value?.revenue, s.value?.currency)}{s.value?.failedPayment ? " ⚠" : ""}
                  </Typography>
                  <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted }}>
                    {s.value?.sessions || 0} session{s.value?.sessions === 1 ? "" : "s"}
                  </Typography>
                </Box>
              )}

              {mods.tutors !== false && (
                <Box sx={{ width: 104, flexShrink: 0 }}>
                  <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: s.nextSession ? textPrimary : textMuted, fontWeight: s.nextSession ? 600 : 400 }}>
                    {s.nextSession ? moment(s.nextSession.at).format("ddd D MMM") : s.request ? `asked ${moment(s.request.at).format("D MMM")}` : "nothing booked"}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}

        </Box>

        {!rows.length && (
          <Box sx={{ px: 1.75, py: 3 }}>
            <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 600, color: textPrimary }}>
              {q ? `Nobody matches “${q}”.` : course === "all" ? "No students yet." : "Nobody on that course."}
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mt: 0.35, lineHeight: 1.5 }}>
              {course === "all" && !q
                ? "Add your first student and they will be emailed an invite to set a password."
                : "That is a real answer, not an empty screen — nobody currently matches."}
            </Typography>
            {course === "all" && !q && (
              <Button
                startIcon={<AddIcon sx={{ fontSize: 17 }} />}
                onClick={() => { setNewStudent({ firstName: "", lastName: "", email: "", course: data?.catalogue?.length === 1 ? data.catalogue[0].license : "", phone: "" }); setAddOpen(true); }}
                sx={{ ...primaryButtonSx, mt: 1.5, minHeight: 0, py: 0.9, px: 2, fontSize: "13px" }}
              >
                Add your first student
              </Button>
            )}
          </Box>
        )}
      </Box>
      )}

      </Box>

      <StudentPopup
        student={popupFor}
        open={Boolean(popupFor)}
        onClose={() => setPopupFor(null)}
        onOpenRecord={() => navigate(`/school/students/${popupFor.id}`)}
        onAction={popupAction}
        viewerRole={userInfo?.role || null}
      />

      <HomeworkModal
        open={homework.open}
        record={homework.record}
        studentId={homework.studentId}
        onClose={() => setHomework({ open: false, record: null, studentId: null, loading: false })}
        onDone={load}
      />

      {/* The instructor's own booked session, in the surface built for it. */}
      <SessionPrep booking={prepFor} open={Boolean(prepFor)} onClose={() => setPrepFor(null)} onDone={load} />

      {/* The same panel the record uses, so proposing from a board and
          proposing from a record are one implementation. */}
      <RecordActionPanel
        open={panel.open}
        mode={panel.mode}
        record={panel.student ? { student: { name: panel.student.name }, weakest: [], value: panel.student.value } : null}
        studentId={panel.student?.id}
        onClose={() => setPanel({ ...panel, open: false })}
        onDone={load}
        // The board has no `weakest` — it is a record-only field — so the note
        // came out empty, and propose_session rejects an empty note. The board
        // does know WHY the student is on it, which is the same sentence a CFI
        // would have written by hand.
        prefill={panel.student ? {
          subject: panel.student.struggle?.subject || "",
          note: panel.student.struggle?.sentence
            ? `${panel.student.struggle.sentence} Let's work through it together.`
            : "",
          taskTitle: panel.student.struggle?.label ? `Work on: ${panel.student.struggle.label}` : "",
        } : null}
        canTeach={TUTOR_CAPABLE.includes(userInfo?.role)}
      />

      {/* Dropping into Struggling records a named human saying so. An
          unexplained flag is a nudge, and a nudge from a named instructor is
          worth less than nothing — it spends the trust that makes the real ones
          land. So the note is asked for, though not compelled. */}
      <Dialog open={Boolean(flagFor)} onClose={() => setFlagFor(null)} PaperProps={{ sx: dialogPaperSx }} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: font, fontSize: "15px", fontWeight: 800 }}>
          Flag {flagFor?.name?.split(" ")[0]} as struggling
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 1.5, lineHeight: 1.5 }}>
            This records your name against a real signal, alongside the automated ones. It does not offer them anything
            or charge them — to do that, open them and propose a session.
          </Typography>
          <TextField
            label="What did you see?" size="small" fullWidth multiline minRows={3}
            value={flagNote} onChange={(e) => setFlagNote(e.target.value)} sx={inputSx}
            placeholder="Rang me about airspace — confident talking about it, lost reading a sectional."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setFlagFor(null)} sx={secondaryButtonSx}>Cancel</Button>
          <Button onClick={() => commitMove(flagFor, "struggling", flagNote)} disabled={movingId === flagFor?.id} sx={primaryButtonSx}>
            {movingId === flagFor?.id ? "Flagging…" : "Flag"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} PaperProps={{ sx: dialogPaperSx }} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: font, fontSize: "15px", fontWeight: 800 }}>Add a student</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <TextField label="First name" size="small" value={newStudent.firstName}
            onChange={(e) => setNewStudent({ ...newStudent, firstName: e.target.value })} sx={inputSx} />
          <TextField label="Last name" size="small" value={newStudent.lastName}
            onChange={(e) => setNewStudent({ ...newStudent, lastName: e.target.value })} sx={inputSx} />
          <TextField label="Email" size="small" type="email" value={newStudent.email}
            onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })} sx={inputSx} />
          <TextField label="Phone (optional)" size="small" value={newStudent.phone}
            onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })} sx={inputSx} />

          {/* Always shown, even with one course on sale.
              A FILTER with one option is furniture — it cannot change what you
              are looking at. A form field with one option is not: it confirms
              what is about to happen to a real person, and silently omitting it
              left you wondering what course they had just been put on. */}
          {(data?.catalogue || []).length > 1 ? (
            <FormControl size="small" sx={inputSx}>
              <InputLabel>Course</InputLabel>
              <Select label="Course" value={newStudent.course}
                onChange={(e) => setNewStudent({ ...newStudent, course: e.target.value })}>
                {data.catalogue.map((c) => (
                  <MenuItem key={c.license} value={c.license}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box sx={{
              px: 1.25, py: 1, borderRadius: "8px", border: `1px solid ${border}`,
              backgroundColor: "#F5F2EF", display: "flex", alignItems: "baseline", gap: 0.75,
            }}>
              <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: textSecondary }}>
                Course
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 600, color: textPrimary, flex: 1 }}>
                {data?.catalogue?.[0]?.label || (isIndependentInstructor ? "Your course" : "Your school's course")}
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted }}>
                the only one you sell
              </Typography>
            </Box>
          )}

          <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, lineHeight: 1.5 }}>
            They will be emailed an invite to set their own password.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setAddOpen(false)} sx={secondaryButtonSx}>Cancel</Button>
          <Button onClick={handleAdd} disabled={adding} sx={primaryButtonSx}>
            {adding ? "Adding…" : "Add student"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
